import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { AllClear, Icon, Sheet } from "@tc/ui";
import { markRead, refreshNotes, useNotes } from "./notes.js";
/** How serious a notification is decides its colour: red, orange, blue or green. */
export const toneOf = (level) => level === "critical" || level === "error" || level === "urgent" ? "red" : level === "warning" || level === "action" ? "amber" : level === "success" ? "green" : "blue";
const MODULE_ICON = {
    "cash-office": "pound", commissions: "percent", operators: "users", fuel: "fuel", wetstock: "droplet", maintenance: "wrench", hr: "users",
    tasks: "tasks", messages: "message", learning: "graduation", audits: "check", budgets: "target", bonuses: "award", "rent-review": "receipt",
    fines: "flag", reports: "chart", sites: "pin", platform: "shield", dashboard: "pulse",
};
const iconOf = (n) => MODULE_ICON[n.module ?? ""] ?? (toneOf(n.level) === "red" ? "alert" : "bell");
const dayKey = (d) => d.toLocaleDateString("en-GB");
function whenOf(iso, now) {
    const d = new Date(iso);
    const mins = Math.round((now.getTime() - d.getTime()) / 60000);
    if (mins < 1)
        return "Now";
    if (mins < 60)
        return `${mins} min`;
    const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    if (dayKey(d) === dayKey(now))
        return time;
    if (now.getTime() - d.getTime() < 6 * 86400000)
        return `${d.toLocaleDateString("en-GB", { weekday: "short" })} ${time}`;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" });
}
function groupOf(iso, now) {
    const d = new Date(iso);
    if (dayKey(d) === dayKey(now))
        return "Today";
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    if (dayKey(d) === dayKey(y))
        return "Yesterday";
    return now.getTime() - d.getTime() < 7 * 86400000 ? "This week" : "Earlier";
}
/** Phones and touch screens get the sheet; a desktop gets a drop-down under the button. */
export function useDesktop() {
    const q = "(min-width: 900px) and (pointer: fine)";
    const [on, setOn] = useState(() => window.matchMedia(q).matches);
    useEffect(() => { const m = window.matchMedia(q); const f = () => setOn(m.matches); m.addEventListener("change", f); return () => m.removeEventListener("change", f); }, []);
    return on;
}
/** A drop-down panel under a top-bar button that closes on a click outside or Escape. */
export function Pop({ open, onClose, anchor, label, className, children }) {
    useEffect(() => {
        if (!open)
            return;
        const click = (e) => { if (!anchor.current?.contains(e.target))
            onClose(); };
        const key = (e) => { if (e.key === "Escape")
            onClose(); };
        document.addEventListener("mousedown", click);
        document.addEventListener("keydown", key);
        return () => { document.removeEventListener("mousedown", click); document.removeEventListener("keydown", key); };
    }, [open, onClose, anchor]);
    if (!open)
        return null;
    return _jsx("div", { className: `tc-pop${className ? ` ${className}` : ""}`, role: "dialog", "aria-label": label, children: children });
}
/** The bell: unread count, and the latest notifications grouped by day, coloured by how serious they are. */
export function Bell({ navigate }) {
    const data = useNotes() ?? { items: [], unread: 0 };
    const [open, setOpen] = useState(false);
    const [only, setOnly] = useState(false);
    const desktop = useDesktop();
    const box = useRef(null);
    useEffect(() => { if (open)
        void refreshNotes(); }, [open]);
    async function go(n) {
        setOpen(false);
        if (!n.readAt)
            void markRead([n.id]);
        if (n.link) {
            if (/^https?:\/\//.test(n.link))
                window.location.assign(n.link);
            else
                navigate(n.link);
        }
    }
    const now = new Date();
    const list = only ? data.items.filter((n) => !n.readAt) : data.items;
    const groups = [];
    for (const n of list) {
        const g = groupOf(n.createdAt, now);
        const last = groups[groups.length - 1];
        if (last && last[0] === g)
            last[1].push(n);
        else
            groups.push([g, [n]]);
    }
    const urgent = data.items.filter((n) => !n.readAt && toneOf(n.level) === "red").length;
    const head = (_jsxs("div", { className: "tc-pop-head", children: [_jsxs("div", { className: "tc-pop-title", children: [_jsx("b", { children: "Notifications" }), data.unread > 0 && _jsxs("span", { className: `tc-pop-count${urgent ? " red" : ""}`, children: [data.unread, " new"] })] }), _jsxs("div", { className: "tc-pop-seg", role: "tablist", children: [_jsx("button", { role: "tab", "aria-selected": !only, className: !only ? "on" : "", onClick: () => setOnly(false), children: "All" }), _jsxs("button", { role: "tab", "aria-selected": only, className: only ? "on" : "", onClick: () => setOnly(true), children: ["Unread", data.unread ? ` · ${data.unread}` : ""] })] })] }));
    const body = list.length === 0
        ? _jsx(AllClear, { icon: "broadcast", title: only ? "Nothing unread" : "You're all caught up", children: only ? "You've read everything. Nice." : "There's nothing new right now. We'll let you know when there's an update." })
        : _jsx("div", { className: "tc-pn-list", children: groups.map(([g, items]) => (_jsxs("section", { children: [_jsx("h3", { className: "tc-pn-day", children: g }), items.map((n, k) => (_jsxs("button", { className: `tc-pn tone-${toneOf(n.level)}${n.readAt ? "" : " unread"}`, style: { ["--k"]: k }, onClick: () => void go(n), children: [_jsx("span", { className: "tc-pn-i", children: _jsx(Icon, { name: iconOf(n), size: 17 }) }), _jsxs("span", { className: "tc-pn-t", children: [_jsx("b", { children: n.title }), n.body && _jsx("small", { children: n.body })] }), _jsxs("span", { className: "tc-pn-m", children: [_jsx("time", { dateTime: n.createdAt, children: whenOf(n.createdAt, now) }), !n.readAt && _jsx("i", { "aria-label": "Unread" })] })] }, n.id)))] }, g))) });
    const foot = data.unread > 0 ? _jsxs("button", { className: "tc-btn", onClick: () => void markRead("all"), children: [_jsx(Icon, { name: "check", size: 16 }), " Mark all as read"] }) : undefined;
    return (_jsxs("div", { className: "tc-bell", ref: box, children: [_jsxs("button", { className: `tc-bell-btn${urgent ? " urgent" : ""}`, "aria-label": `Notifications${data.unread ? `, ${data.unread} unread` : ""}`, "aria-expanded": open, onClick: () => setOpen(!open), children: [_jsxs("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", "aria-hidden": "true", children: [_jsx("path", { d: "M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" }), _jsx("path", { d: "M10.3 21a1.94 1.94 0 0 0 3.4 0" })] }), data.unread > 0 && _jsx("span", { className: `tc-bell-count${urgent ? "" : " calm"}`, children: data.unread > 99 ? "99+" : data.unread })] }), desktop
                ? _jsxs(Pop, { open: open, onClose: () => setOpen(false), anchor: box, label: "Notifications", className: "tc-pop-notes", children: [head, _jsx("div", { className: "tc-pop-body", children: body }), foot && _jsx("div", { className: "tc-pop-foot", children: foot })] })
                : _jsxs(Sheet, { open: open, onClose: () => setOpen(false), title: "Notifications", foot: foot, children: [head, body] })] }));
}
